import authService from './authService';
import {
  GoodsDeliveryOrder,
  GoodsDeliveryOrderPayload,
  GoodsDeliveryOrderPendingCount,
  GoodsDeliveryOrderSearchModel,
} from '../types/goodsDeliveryOrder';
import { PaginatedResponse, ApiResponse } from '../types/common';

/** Lê a mensagem de erro do envelope da API (sem quebrar quando o corpo não é JSON). */
const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return (body && typeof body.message === 'string' && body.message) || fallback;
};

class GoodsDeliveryOrderService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── List ───────────────────────────────────────────────────

  async getOrders(searchModel: GoodsDeliveryOrderSearchModel): Promise<PaginatedResponse<GoodsDeliveryOrder>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.status && { status: searchModel.status }),
      ...(searchModel.is_emergency && { is_emergency: 'true' }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-delivery-orders?${params}`);
    if (!response.ok) throw new Error(await readErrorMessage(response, 'Falha ao buscar ordens de entrega'));

    const json = await response.json();
    // Paginação pode vir na raiz ({ success, data, pagination }) ou aninhada em data.
    const pagination = json.pagination ?? json.data?.pagination;
    return {
      success: json.success,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: pagination?.page ?? searchModel.page,
        limit: pagination?.page_size ?? searchModel.pageSize,
        total: pagination?.total_items ?? 0,
        totalPages: pagination?.total_pages ?? 1,
      },
    };
  }

  // ─── Get by ID ──────────────────────────────────────────────

  async getOrderById(id: number): Promise<GoodsDeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-delivery-orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de entrega não encontrada');
      throw new Error(await readErrorMessage(response, 'Falha ao buscar ordem de entrega'));
    }
    const data: ApiResponse<GoodsDeliveryOrder> = await response.json();
    return data.data!;
  }

  // ─── Create ─────────────────────────────────────────────────

  async createOrder(payload: GoodsDeliveryOrderPayload): Promise<GoodsDeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/goods-delivery-orders', payload);
    if (!response.ok) throw new Error(await readErrorMessage(response, 'Falha ao criar ordem de entrega'));
    const data: ApiResponse<GoodsDeliveryOrder> = await response.json();
    return data.data!;
  }

  // ─── Update ─────────────────────────────────────────────────

  /** `items` presente substitui a lista inteira (mínimo 1). 409 = entregue/cancelada. */
  async updateOrder(id: number, payload: Partial<GoodsDeliveryOrderPayload>): Promise<GoodsDeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/goods-delivery-orders/${id}`, payload);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de entrega não encontrada');
      if (response.status === 409) {
        throw new Error(await readErrorMessage(response, 'A ordem já foi entregue ou cancelada e não pode ser alterada'));
      }
      throw new Error(await readErrorMessage(response, 'Falha ao atualizar ordem de entrega'));
    }
    const data: ApiResponse<GoodsDeliveryOrder> = await response.json();
    return data.data!;
  }

  // ─── Cancel ─────────────────────────────────────────────────

  /** Motivo obrigatório (máx. 500). Remove a parada de rota ainda não entregue. */
  async cancelOrder(id: number, reason: string): Promise<GoodsDeliveryOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/goods-delivery-orders/${id}/cancel`, { reason });
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de entrega não encontrada');
      if (response.status === 409) throw new Error(await readErrorMessage(response, 'A ordem já foi entregue ou cancelada'));
      throw new Error(await readErrorMessage(response, 'Falha ao cancelar ordem de entrega'));
    }
    const data: ApiResponse<GoodsDeliveryOrder> = await response.json();
    return data.data!;
  }

  // ─── Delete ─────────────────────────────────────────────────

  async deleteOrder(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/goods-delivery-orders/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Ordem de entrega não encontrada');
      if (response.status === 409) throw new Error(await readErrorMessage(response, 'Ordem já entregue não pode ser excluída'));
      throw new Error(await readErrorMessage(response, 'Falha ao excluir ordem de entrega'));
    }
  }

  // ─── Count pending (sino) ───────────────────────────────────

  /** Falha (ex.: sem permissão) devolve zeros — o sino não é crítico. */
  async getPendingCount(): Promise<GoodsDeliveryOrderPendingCount> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/goods-delivery-orders/pending-count');
    if (!response.ok) return { pending: 0, emergency: 0 };
    const data: ApiResponse<GoodsDeliveryOrderPendingCount> = await response.json();
    return {
      pending: data.data?.pending ?? 0,
      emergency: data.data?.emergency ?? 0,
    };
  }

  // ─── Pending for route (Gerenciar Rotas) ────────────────────

  /** Pendentes, não canceladas e sem parada em rota (sem paginação). */
  async getPendingForRoute(): Promise<GoodsDeliveryOrder[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/goods-delivery-orders/pending-for-route');
    if (!response.ok) throw new Error(await readErrorMessage(response, 'Falha ao buscar ordens de entrega pendentes'));
    const json = await response.json();
    return Array.isArray(json.data) ? json.data : (Array.isArray(json.data?.data) ? json.data.data : []);
  }

  // ─── Delivery photo (comprovante) ───────────────────────────

  /**
   * Baixa a foto do comprovante com a sessão autenticada e devolve uma object URL
   * (o chamador revoga com URL.revokeObjectURL ao desmontar).
   */
  async getDeliveryPhotoObjectUrl(id: number, idx: 1 | 2): Promise<string> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/goods-delivery-orders/${id}/delivery-photos/${idx}`);
    if (!response.ok) throw new Error(`Falha ao carregar a foto ${idx} do comprovante`);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }
}

export const goodsDeliveryOrderService = new GoodsDeliveryOrderService();
export default goodsDeliveryOrderService;
